import webClient from "../config/api.config";
import i18n from "../i18n";
import {
  OnboardingPromotionApplyRequest,
  PromotionAssignRequest,
  PromotionCodeCreateRequest,
  PromotionPeriodCreateRequest,
  PromotionUnassignRequest,
} from "../types/promotion.definition";

// NOTE: webClient's baseURL already carries the `/api/v1` prefix (see
// createEnvConfig in @ews-admin/global-design-system), so paths below omit it
// — matching the convention used by every other webclient in this folder
// (e.g. licence.client.ts's `/licences/plans`).

export function getPromotionCodes() {
  return webClient.request({ method: "get", url: "/admin/promotion/codes" });
}

export function createPromotionCode(data: PromotionCodeCreateRequest) {
  return webClient.request({
    method: "post",
    url: "/admin/promotion/code",
    data,
  });
}

// MED-1441: real deletion of a promotion code (and everything hanging off it).
// `id` is the promotion *code*'s id — not the period's `promotionKey`.
export function deletePromotionCode(id: number) {
  return webClient.request({
    method: "delete",
    url: `/admin/promotion/code/${id}`,
  });
}

export function createPromotionPeriod(data: PromotionPeriodCreateRequest) {
  return webClient.request({ method: "post", url: "/admin/promotion", data });
}

export function assignPromotion(data: PromotionAssignRequest) {
  return webClient.request({
    method: "post",
    url: "/admin/promotion/assign",
    data,
  });
}

export function getOnboardingPromotions() {
  return webClient.request({ method: "get", url: "/onboarding/promotions" });
}

// MED-1539: no longer used by the onboarding wizard. This endpoint creates a
// NEW validity period for the code out of the dates it is given and attaches the
// facility to that one, which left the promotion the admin had picked with no
// facility at all. The wizard now assigns to the selected period through
// assignPromotion above, exactly like the Promotions page does. Kept here (and
// pinned by a test asserting the wizard does not call it) until the endpoint's
// own fate is decided on the medecine360-api side.
export function applyOnboardingPromotion(
  data: OnboardingPromotionApplyRequest
) {
  return webClient.request({
    method: "post",
    url: "/onboarding/promotions/apply",
    data,
  });
}

export function getPromotionFacilities(promotionKey: number) {
  return webClient.request({
    method: "get",
    url: `/admin/promotion/${promotionKey}/facilities`,
  });
}

export function unassignPromotion(data: PromotionUnassignRequest) {
  return webClient.request({
    method: "post",
    url: "/admin/promotion/unassign",
    data,
  });
}

export function updatePromotionPeriod(
  promotionKey: number,
  data: { startDate: string; endDate: string }
) {
  return webClient.request({
    method: "put",
    url: `/admin/promotion/${promotionKey}`,
    data,
  });
}

// MED-1583: promotions active today for a facility (the BFF localises the
// label from ?lang).
export function getActiveFacilityPromotions(facilityId: number) {
  return webClient.request({
    method: "get",
    url: `/promotion/facility/${facilityId}`,
    params: { lang: i18n.language || "fr" },
  });
}
